import React, { useState, useEffect } from 'react';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import Upload from 'lucide-react/dist/esm/icons/upload';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Info from 'lucide-react/dist/esm/icons/info';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check';
import UsersIcon from 'lucide-react/dist/esm/icons/users';
import ExternalLink from 'lucide-react/dist/esm/icons/external-link';
import Heart from 'lucide-react/dist/esm/icons/heart';
import Eye from 'lucide-react/dist/esm/icons/eye';
import Download from 'lucide-react/dist/esm/icons/download';
import Share2 from 'lucide-react/dist/esm/icons/share-2';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { motion } from 'framer-motion';
import { Modal } from '../common/Modal';

interface HealthcareDirective {
  id: string;
  type: 'advance_care_directive' | 'healthcare_proxy';
  principal_name: string;
  principal_address: string;
  principal_phone: string;
  principal_email: string;
  agent_name?: string;
  agent_address?: string;
  agent_phone?: string;
  agent_email?: string;
  medical_preferences: string[];
  life_sustaining_treatment: string;
  organ_donation: string;
  effective_date: string;
  review_date?: string;
  document_url?: string;
}

interface ACDDocument {
  id: string;
  user_id: string;
  title: string;
  type: string;
  status: string;
  content: string;
  file_path?: string;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  file_url?: string;
  last_reviewed?: string;
  shared_with?: string[];
  created_at: string;
  updated_at: string;
}

export function AdvanceCareDirectives() {
  const { user } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadType, setUploadType] = useState<'advance_care_directive' | 'healthcare_proxy'>('advance_care_directive');
  const [documents, setDocuments] = useState<ACDDocument[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<HealthcareDirective | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareDocId, setShareDocId] = useState<string | null>(null);
  const [shareForm, setShareForm] = useState({
    email: '',
    accessLevel: 'view',
    note: ''
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('File type not supported. Please upload PDF, DOC, DOCX, or image files.');
      return;
    }
    try {
      setError(null);
      setUploading(true);
      const filePath = `${user?.id}/acd/${Date.now()}_${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('legal-documents')
        .upload(filePath, file);
      if (uploadError) throw uploadError;
      
      // Generate a signed URL for the uploaded file
      const { data: urlData, error: urlError } = await supabase.storage
        .from('legal-documents')
        .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year expiry
      if (urlError) throw urlError;
      
      const { error: dbError } = await supabase
        .from('legal_documents')
        .insert([
          {
            user_id: user?.id,
            title: `Uploaded ${uploadType === 'advance_care_directive' ? 'Advance Care Directive' : 'Healthcare Proxy'} - ${file.name}`,
            type: 'acd',
            status: 'active',
            content: JSON.stringify({ file_path: filePath }),
            file_path: filePath,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            file_url: urlData.signedUrl,
            last_reviewed: new Date().toISOString(),
          },
        ]);
      if (dbError) throw dbError;
      setShowUploadModal(false);
      fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const fetchDocuments = async (background = false) => {
    try {
      if (!background) setLoading(true);
      if (!user) return;
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'acd')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to fetch documents');
    } finally {
      if (!background) setLoading(false);
    }
  };

  const handleDownload = async (doc: ACDDocument) => {
    try {
      setDownloading(doc.id);
      if (!doc.file_path) {
        throw new Error('No file path available');
      }
      
      // Generate a fresh signed URL for downloading
      const { data: urlData, error: urlError } = await supabase.storage
        .from('legal-documents')
        .createSignedUrl(doc.file_path, 60 * 60); // 1 hour expiry for downloading
      
      if (urlError) throw urlError;
      
      const response = await fetch(urlData.signedUrl);
      if (!response.ok) throw new Error('Failed to download file');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (doc: ACDDocument) => {
    try {
      setDeleting(doc.id);
      
      // Delete from storage if file exists
      if (doc.file_path) {
        const { error: storageError } = await supabase.storage
          .from('legal-documents')
          .remove([doc.file_path]);
        if (storageError) console.error('Storage delete error:', storageError);
      }
      
      // Delete from database
      const { error } = await supabase
        .from('legal_documents')
        .delete()
        .eq('id', doc.id);
      
      if (error) throw error;
      
      fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setDeleting(null);
    }
  };

  const handleView = async (doc: ACDDocument) => {
    try {
      if (!doc.file_path) {
        setError('No file path available');
        return;
      }
      
      // Use public URL for viewing since bucket is public
      const { data } = supabase.storage
        .from('legal-documents')
        .getPublicUrl(doc.file_path);
      
      if (data.publicUrl) {
        // Check if it's a PDF to show in built-in viewer
        if (doc.file_type === 'application/pdf') {
          setPdfViewerUrl(data.publicUrl);
        } else {
          window.open(data.publicUrl, '_blank');
        }
      } else {
        setError('Unable to generate view URL for document');
      }
    } catch (err) {
      setError('Failed to generate view URL for document');
    }
  };

  const handleShare = (doc: ACDDocument) => {
    setShareDocId(doc.id);
    setShowShareModal(true);
  };

  const handleShareFormChange = (field: string, value: string) => {
    setShareForm(prev => ({ ...prev, [field]: value }));
  };

  const handleShareSubmit = async () => {
    try {
      if (!shareDocId) return;
      
      // Here you would implement the actual sharing logic
      // For now, we'll just show a success message
      console.log('Sharing document:', shareDocId, shareForm);
      
      setShowShareModal(false);
      setShareForm({ email: '', accessLevel: 'view', note: '' });
      setShareDocId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share document');
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <FileText className="w-6 h-6 text-red-600" />;
    if (fileType.includes('word') || fileType.includes('document')) return <FileText className="w-6 h-6 text-blue-600" />;
    if (fileType.includes('image')) return <FileText className="w-6 h-6 text-green-600" />;
    return <FileText className="w-6 h-6 text-gray-600" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    if (user) {
      fetchDocuments();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) fetchDocuments(true); };
    window.addEventListener('refresh-legal', handler);
    return () => window.removeEventListener('refresh-legal', handler);
  }, [user]);

  return (
    <div className="space-y-8">
      {/* Header & Quick Actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Heart className="text-red-600" size={28} /> Advance Care Directives
          </h2>
          <p className="text-gray-600 mt-1 max-w-xl">
            Access official Advance Care Directive forms from your state government and manage your existing healthcare planning documents securely.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowUploadModal(true)}>
            <Upload className="w-4 h-4 mr-2" /> Upload Existing
          </Button>
        </div>
      </div>

      {/* Advance Care Planning Laws */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <ExternalLink className="text-red-600" size={24} />
          <h3 className="text-xl font-semibold text-gray-900">Advance Care Planning Laws</h3>
        </div>
        <p className="text-gray-600 mb-6">
          For comprehensive information about Advance Care Planning laws across all Australian states and territories, please visit the official Advance Care Planning Australia website.
        </p>
        <a
          href="https://www.advancecareplanning.org.au/law-and-ethics/state-and-territory-laws/advance-care-planning-laws-in-nsw"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          <ExternalLink size={16} />
          View Advance Care Planning Laws
        </a>
      </Card>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="flex items-center gap-4 p-4">
          <ShieldCheck className="text-green-600" size={28} />
          <div>
            <div className="font-semibold">Active Directives</div>
            <div className="text-2xl font-bold text-green-700">{documents.filter(d => d.status === 'active').length}</div>
          </div>
        </Card>
        <Card className="flex items-center gap-4 p-4">
          <UsersIcon className="text-blue-600" size={28} />
          <div>
            <div className="font-semibold">Total Documents</div>
            <div className="text-2xl font-bold text-blue-700">{documents.length}</div>
          </div>
        </Card>
        <Card className="flex items-center gap-4 p-4">
          <Info className="text-amber-600" size={28} />
          <div>
            <div className="font-semibold">Needs Review</div>
            <div className="text-2xl font-bold text-amber-700">{documents.filter(d => d.last_reviewed && new Date(d.last_reviewed) < new Date(Date.now() - 1000*60*60*24*365)).length}</div>
          </div>
        </Card>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <span className="animate-spin mr-2">🔄</span> Loading healthcare directives...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" /> {error}
        </div>
      )}

      {/* Document List */}
      {!loading && (
        <>
          {documents.length === 0 ? (
            <Card className="p-8 text-center flex flex-col items-center gap-4">
              <Heart className="text-red-400 mx-auto" size={48} />
              <h3 className="text-xl font-semibold">No Advance Care Directives yet</h3>
              <p className="text-gray-500 max-w-md mx-auto">Upload your existing healthcare directives to keep them organised and easily accessible. Use the state government forms above to create new documents.</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => setShowUploadModal(true)}>
                  <Upload className="w-4 h-4 mr-2" /> Upload Document
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold mb-4">Your Documents</h2>
                <div className="space-y-4">
                  {documents.map((doc) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between p-6 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg">
                          {getFileIcon(doc.file_type || '')}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg">{doc.title}</h3>
                          <div className="flex items-center gap-6 text-sm text-gray-600 mt-1">
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                              {doc.type === 'advance_care_directive' ? 'Advance Care' : 'Healthcare Proxy'}
                            </span>
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                              {doc.status}
                            </span>
                            {doc.file_size && <span className="flex items-center gap-1"><FileText size={14} /> {formatFileSize(doc.file_size)}</span>}
                            {doc.last_reviewed && (
                              <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(doc.last_reviewed).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          size="md" 
                          onClick={() => handleView(doc)} 
                          disabled={deleting === doc.id}
                          className="flex items-center gap-2 min-w-[100px]"
                        >
                          <Eye size={18} />
                          {doc.file_type === 'application/pdf' ? 'View PDF' : 'View'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="md" 
                          onClick={() => handleDownload(doc)} 
                          disabled={deleting === doc.id || downloading === doc.id}
                          className="flex items-center gap-2 min-w-[120px]"
                        >
                          {downloading === doc.id ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin" />
                          ) : (
                            <Download size={18} />
                          )}
                          {downloading === doc.id ? 'Downloading...' : 'Download'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="md" 
                          onClick={() => handleShare(doc)} 
                          disabled={deleting === doc.id}
                          className="flex items-center gap-2 min-w-[100px]"
                        >
                          <Share2 size={18} />
                          Share
                        </Button>
                        <Button 
                          variant="outline" 
                          size="md" 
                          onClick={() => handleDelete(doc)} 
                          disabled={deleting === doc.id}
                          className="flex items-center gap-2 min-w-[100px] border-red-200 text-red-600 hover:bg-red-50"
                        >
                          {deleting === doc.id ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin" />
                          ) : (
                            <Trash2 size={18} />
                          )}
                          {deleting === doc.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Tips & Resources */}
      <Card className="p-6 mt-8 bg-red-50 border-red-100">
        <div className="flex items-center gap-3 mb-2">
          <Info className="text-red-600" />
          <span className="font-semibold text-red-900">Important Information</span>
        </div>
        <ul className="list-disc pl-6 text-sm text-red-900 space-y-1">
          <li>Always consult with healthcare providers and legal professionals when creating Advance Care Directives.</li>
          <li>State government forms are legally recognized and regularly updated.</li>
          <li>Review your directives annually or after major health changes.</li>
          <li>Ensure your healthcare agent is someone you trust and understands your wishes.</li>
          <li>Share copies of your directives with family, healthcare providers, and your agent.</li>
          <li>Keep your directives easily accessible in case of emergency.</li>
        </ul>
      </Card>

      {/* Upload Modal */}
      {showUploadModal && (
        <Modal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          title={`Upload ${uploadType === 'advance_care_directive' ? 'Advance Care Directive' : 'Healthcare Proxy'} Document`}
        >
          <div className="space-y-6">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                onChange={handleUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="hidden"
                id="file-upload-acd"
                disabled={uploading}
              />
              <label
                htmlFor="file-upload-acd"
                className={`cursor-pointer flex flex-col items-center justify-center ${uploading ? 'opacity-50' : ''}`}
              >
                {uploading ? (
                  <div className="w-12 h-12 border-4 border-gray-300 border-t-red-600 rounded-full animate-spin mb-4" />
                ) : (
                  <Upload size={40} className="text-gray-400 mb-4" />
                )}
                <p className="text-sm text-gray-600">
                  {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PDF, DOC, DOCX, or images (max. 50MB)
                </p>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Document Type</label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value as 'advance_care_directive' | 'healthcare_proxy')}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                disabled={uploading}
              >
                <option value="advance_care_directive">Advance Care Directive</option>
                <option value="healthcare_proxy">Healthcare Proxy</option>
              </select>
            </div>
            {error && (
              <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
                <AlertCircle size={20} />
                <p>{error}</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* PDF Viewer Modal */}
      {pdfViewerUrl && (
        <Modal
          isOpen={!!pdfViewerUrl}
          onClose={() => setPdfViewerUrl(null)}
          title="Document Viewer"
          maxWidth="2xl"
        >
          <div className="h-96">
            <iframe
              src={pdfViewerUrl}
              className="w-full h-full border-0"
              title="Document Viewer"
            />
          </div>
        </Modal>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <Modal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title="Share Document"
          maxWidth="md"
        >
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={shareForm.email}
                onChange={(e) => handleShareFormChange('email', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter email address"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Access Level</label>
              <select
                value={shareForm.accessLevel}
                onChange={(e) => handleShareFormChange('accessLevel', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="view">View Only</option>
                <option value="edit">Edit</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note (Optional)</label>
              <textarea
                value={shareForm.note}
                onChange={(e) => handleShareFormChange('note', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
                placeholder="Add a note about this document..."
              />
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowShareModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleShareSubmit}
                className="flex-1"
              >
                Share Document
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}